import ServiceError from "./ServiceError"

/** Universal api client wrapper for JSON REST APIs */
export default class ApiClient {
  constructor(serviceName, baseURL) {
    this.serviceName = serviceName
    this.baseURL = baseURL
  }

  _getUrl(path) {
    return `${this.baseURL}${path}`
  }

  /**
   * Executes an api request
   * @param method HTTP method
   * @param path The api path after the base URL
   * @param {function} dataMapper An optional function to validate and map the json.
   *   The dataMapper function should throw a ServiceError if the data is not valid.
   * @return {Promise<*>}
   * @private
   */
  async _execRequest(method, path, dataMapper = null) {
    let response = null
    try {
      response = await fetch(this._getUrl(path), {
        method,
        mode: "cors",
        credentials: "include",
      })
    } catch (err) {
      console.error(
        `${this.serviceName} service: Unable to ${method} ${path}`,
        err
      )
      throw new ServiceError(ServiceError.STATUS_CONNECTION_ERROR, err.message)
    }
    if (!response.ok) {
      const message = `${
        this.serviceName
      } service: ${method} ${path} returned status ${response.status}`
      console.error(message)
      throw new ServiceError(response.status, message)
    }

    let data = null
    try {
      data = await response.json()
    } catch (err) {
      const message = `${
        this.serviceName
      } service: ${method} ${path} returned invalid json`
      console.error(message, err)
      throw new ServiceError(ServiceError.STATUS_INVALID_DATA, message)
    }

    if (dataMapper) {
      return await dataMapper(data)
    }
    return data
  }

  /**
   * Execute a GET request
   * @param {string} path The api path after the base path
   * @param {function} dataMapper The optional data mapping function.
   * @return {Promise<*>}
   */
  async get(path, dataMapper = null) {
    return this._execRequest("GET", path, dataMapper)
  }

  /**
   * Execute a PUT request
   * @param {string} path The api path after the base path
   * @param {function} dataMapper The optional data mapping function.
   * @return {Promise<*>}
   */
  async put(path, dataMapper = null) {
    return this._execRequest("PUT", path, dataMapper)
  }

  /**
   * Execute a DELETE request
   * @param {string} path The api path after the base path
   * @param {function} dataMapper The optional data mapping function.
   * @return {Promise<*>}
   */
  async delete(path, dataMapper = null) {
    return this._execRequest("DELETE", path, dataMapper)
  }
}
